import type { PlantillaInput } from '@app/shared';
import { useMutation } from '@tanstack/react-query';
import { Plus, Send } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { emisionApi } from '../api/emision.api';

// ciclo-emisor, parte 2: "2. Mensajes", "3. Ritmo" y "4. Probar".

/** La misma regla que el motor (backend lib/emisor.ts, `armarTexto`): primer nombre,
 * y sin nombre se saca el "Hola {{nombre}}," entero. */
export function armarTexto(plantilla: string, nombre: string | null): string {
  const n = (nombre ?? '').trim().split(/\s+/)[0] ?? '';
  const texto = n
    ? plantilla.replace(/\{\{\s*nombre\s*\}\}/gi, n)
    : plantilla.replace(/Hola\s+\{\{\s*nombre\s*\}\}\s*[,!]?\s*/gi, '').replace(/\{\{\s*nombre\s*\}\}/gi, '');
  return texto.trim();
}

export function PlantillasForm({
  plantillas,
  onChange,
}: {
  plantillas: PlantillaInput[];
  onChange: (p: PlantillaInput[]) => void;
}): JSX.Element {
  const t = useI18n().campanasEnvio.plantillas;
  const cambiar = (i: number, parcial: Partial<PlantillaInput>): void =>
    onChange(plantillas.map((p, j) => (j === i ? { ...p, ...parcial } : p)));

  return (
    <div className="space-y-4">
      <p className="text-sm text-text-subtle">{t.ayuda}</p>
      {plantillas.map((p, i) => (
        <div
          key={p.id ?? `nueva-${String(i)}`}
          className="grid grid-cols-2 gap-4 rounded-xl border border-border-subtle p-4"
        >
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Input
                aria-label={t.nombre}
                value={p.nombre}
                onChange={(e) => cambiar(i, { nombre: e.target.value })}
                className="h-9 max-w-xs"
              />
              <label className="flex items-center gap-2 text-sm text-text-subtle">
                <input
                  type="checkbox"
                  checked={p.activa}
                  onChange={(e) => cambiar(i, { activa: e.target.checked })}
                />
                {t.activa}
              </label>
            </div>
            <Textarea
              aria-label={t.texto}
              rows={5}
              value={p.texto}
              onChange={(e) => cambiar(i, { texto: e.target.value })}
            />
          </div>
          <div className="space-y-2 text-sm">
            <div className="text-xs text-text-faint">{t.vistaPrevia}</div>
            <Burbuja texto={armarTexto(p.texto, 'Ana María')} />
            <div className="text-xs text-text-faint">{t.vistaPreviaSinNombre}</div>
            <Burbuja texto={armarTexto(p.texto, null)} />
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        onClick={() =>
          onChange([
            ...plantillas,
            {
              nombre: t.nueva.replace('{n}', String(plantillas.length + 1)),
              texto: 'Hola {{nombre}}, ',
              activa: true,
            },
          ])
        }
      >
        <Plus size={16} />
        {t.agregar}
      </Button>
    </div>
  );
}

function Burbuja({ texto }: { texto: string }): JSX.Element {
  return (
    <div className="max-w-md whitespace-pre-wrap rounded-lg rounded-tl-none bg-success/10 px-3 py-2 text-text-strong">
      {texto || '-'}
    </div>
  );
}

export type Ritmo = { topeDiario: number; intervaloMin: number; horaInicio: number; horaFin: number };

export function RitmoForm({ ritmo, onChange }: { ritmo: Ritmo; onChange: (r: Ritmo) => void }): JSX.Element {
  const t = useI18n().campanasEnvio.ritmo;
  const numero = (campo: keyof Ritmo, min: number, max: number) => (
    <Input
      type="number"
      min={min}
      max={max}
      className="h-9 w-20"
      value={ritmo[campo]}
      onChange={(e) =>
        onChange({ ...ritmo, [campo]: Math.min(max, Math.max(min, Number(e.target.value) || min)) })
      }
    />
  );
  return (
    <div className="space-y-3 text-sm text-text-subtle">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
        <label className="flex items-center gap-2">
          {t.topeDiario} {numero('topeDiario', 1, 50)}
        </label>
        <label className="flex items-center gap-2">
          {t.intervalo} {numero('intervaloMin', 5, 240)}
        </label>
        <span className="flex items-center gap-2">
          {t.franja} {t.de} {numero('horaInicio', 0, 23)} {t.a} {numero('horaFin', 1, 24)} {t.horas}
        </span>
      </div>
      <p className="text-xs text-text-faint">{t.ayudaTope}</p>
    </div>
  );
}

/** "Enviarme una prueba": el texto elegido, ya, al número de quien está armando la campaña. */
export function ProbarEnvio({
  plantillas,
  numeroInicial,
}: {
  plantillas: PlantillaInput[];
  numeroInicial: string | null;
}): JSX.Element {
  const es = useI18n();
  const t = es.campanasEnvio.prueba;
  const [numero, setNumero] = useState(numeroInicial ?? '');
  const [indice, setIndice] = useState(0);
  const elegida = plantillas[indice] ?? plantillas[0];
  const enviar = useMutation({
    mutationFn: () => emisionApi.prueba({ numero, texto: elegida?.texto ?? '' }),
    onSuccess: (r) => toast.success(t.enviada.replace('{instancia}', r.instancia)),
    onError: (e) => toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });
  return (
    <div className="space-y-3">
      <p className="text-sm text-text-subtle">{t.ayuda}</p>
      <div className="flex flex-wrap items-center gap-3 text-sm text-text-subtle">
        <label className="flex items-center gap-2">
          {t.numero}
          <Input
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            placeholder="+54 9 ..."
            className="h-9 w-48"
          />
        </label>
        {plantillas.length > 1 && (
          <label className="flex items-center gap-2">
            {t.plantilla}
            <select
              value={indice}
              onChange={(e) => setIndice(Number(e.target.value))}
              className="h-9 rounded-md border border-marca-neutro bg-surface px-2 text-sm text-marca-gris"
            >
              {plantillas.map((p, i) => (
                <option key={p.id ?? i} value={i}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </label>
        )}
        <Button
          type="button"
          variant="outline"
          disabled={
            enviar.isPending || numero.trim().length < 8 || !elegida || elegida.texto.trim().length < 5
          }
          onClick={() => enviar.mutate()}
        >
          <Send size={16} />
          {t.enviar}
        </Button>
      </div>
    </div>
  );
}
